import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';
import { OrgWidgetData, OrgWidgetMenuItem } from '../../models/org-widget.models';
import { SecurityFlowDialogComponent } from './security-flow-dialog.component';
import { OrgEntityColorsService } from '../../services/org-entity-colors.service';

/** Represents a single route with its associated features and permissions. */
interface RouteFeatureNode {
    /** Display-friendly route path (e.g. "/organization") */
    route: string;
    /** The routeFeature key this route guards against */
    routeFeature: string;
    /** Whether it's a system route */
    systemRoute: boolean;
    /** Whether any authenticated user can access this route */
    publicRoute: boolean;
    /** Features linked to this route + their permissions */
    features: FeatureNode[];
    /** Whether this node is expanded in the tree */
    expanded: boolean;
    /** Menu item info if this route maps to a sidebar menu item */
    menuItem?: OrgWidgetMenuItem;
    /** Section name extracted from route path (e.g. 'Organization', 'Management Tools') */
    section?: string;
}

interface FeatureNode {
    key: string;
    name: string;
    permissions: string[];
    enabled: boolean;
    /** Is this the primary routeFeature (vs an additional feature on the route)? */
    isPrimary: boolean;
    /** Feature description from the database */
    description?: string;
    /** Per-permission descriptions from the database */
    permissionDescriptions?: Record<string, string>;
}

@Component({
    selector: 'shared-feature-map-widget',
    imports: [CommonModule, MatIconModule, MatTooltipModule, MatDialogModule, FormsModule],
    template: `
        <!-- Search bar -->
        <div class="search-bar">
            <mat-icon class="search-icon">search</mat-icon>
            <input
                type="text"
                [(ngModel)]="searchTerm"
                (ngModelChange)="applyFilter()"
                placeholder="Search routes, features, permissions..."
                class="search-input"
            />
            <mat-icon *ngIf="searchTerm" class="clear-icon" (click)="searchTerm = ''; applyFilter()">close</mat-icon>
        </div>

        <!-- Summary -->
        <div class="summary-bar">
            <span class="summary-item" matTooltip="Total routes">
                <mat-icon>route</mat-icon> {{ allNodes.length }}
            </span>
            <span class="summary-item" matTooltip="Total features">
                <mat-icon [style.color]="colorsService.getColor('features')">extension</mat-icon> {{ totalFeatures }}
            </span>
            <span class="summary-item" matTooltip="Total unique permissions">
                <mat-icon [style.color]="colorsService.getColor('permissions')">vpn_key</mat-icon>
                {{ totalPermissions }}
            </span>
            <span class="spacer"></span>
            <mat-icon
                class="action-icon help-icon"
                (click)="openSecurityFlow($event)"
                matTooltip="How the security system works"
                >help_outline</mat-icon
            >
            <mat-icon class="action-icon" (click)="expandAll()" matTooltip="Expand all">unfold_more</mat-icon>
            <mat-icon class="action-icon" (click)="collapseAll()" matTooltip="Collapse all">unfold_less</mat-icon>
        </div>

        <!-- Tree -->
        <div class="tree-container">
            <div *ngFor="let node of filteredNodes; trackBy: trackByRoute" class="route-node">
                <!-- Route row -->
                <div class="route-row" (click)="node.expanded = !node.expanded">
                    <mat-icon class="expand-icon">{{ node.expanded ? 'expand_more' : 'chevron_right' }}</mat-icon>
                    <mat-icon
                        class="route-icon"
                        [class.system]="node.systemRoute"
                        [style.color]="
                            node.publicRoute
                                ? '#4caf50'
                                : node.systemRoute
                                  ? colorsService.getColor('securityGroups')
                                  : colorsService.getColor('features')
                        "
                        [matTooltip]="
                            node.publicRoute
                                ? 'Public route (all authenticated users)'
                                : node.systemRoute
                                  ? 'System route (internal/framework)'
                                  : 'User-facing route'
                        "
                    >
                        {{ node.publicRoute ? 'public' : node.systemRoute ? 'settings' : 'link' }}
                    </mat-icon>
                    <span class="route-path" [innerHTML]="highlight(node.route)"></span>
                    <span
                        *ngIf="node.section"
                        class="section-label"
                        [style.color]="colorsService.getChipTextColor('securityGroups')"
                        [style.background]="colorsService.getBackground('securityGroups')"
                        [matTooltip]="'Section: ' + node.section"
                        >{{ node.section }}</span
                    >
                    <span
                        *ngIf="node.menuItem"
                        class="menu-label"
                        [style.color]="colorsService.getChipTextColor('features')"
                        [style.background]="colorsService.getBackground('features')"
                        [matTooltip]="'Sidebar menu: ' + node.menuItem.name"
                    >
                        <mat-icon *ngIf="node.menuItem.icon" class="menu-label-icon">{{ node.menuItem.icon }}</mat-icon>
                        <span [innerHTML]="highlight(node.menuItem.name)"></span>
                    </span>
                    <span class="feature-count" matTooltip="Features on this route">{{ node.features.length }}</span>
                </div>

                <!-- Features list (expanded) -->
                <div *ngIf="node.expanded" class="features-list">
                    <div *ngFor="let feat of node.features; trackBy: trackByFeature" class="feature-row">
                        <mat-icon
                            class="feature-icon"
                            [class.primary]="feat.isPrimary"
                            [class.disabled]="!feat.enabled"
                            [style.color]="feat.enabled ? colorsService.getColor('features') : null"
                            [matTooltip]="feat.isPrimary ? 'Primary route feature' : 'Additional feature'"
                        >
                            {{ feat.isPrimary ? 'star' : 'extension' }}
                        </mat-icon>
                        <span
                            class="feature-name"
                            [class.disabled]="!feat.enabled"
                            [matTooltip]="feat.description || ''"
                            [innerHTML]="highlight(feat.name || feat.key)"
                        ></span>

                        <!-- Permissions chips -->
                        <div class="permission-chips">
                            <span
                                *ngFor="let perm of feat.permissions"
                                class="permission-chip"
                                [style.color]="colorsService.getChipTextColor('permissions')"
                                [style.background]="colorsService.getBackground('permissions')"
                                [matTooltip]="feat.permissionDescriptions?.[perm] || ''"
                                [innerHTML]="highlight(perm)"
                            >
                            </span>
                            <span *ngIf="!feat.permissions.length" class="no-permissions">No permissions</span>
                        </div>
                    </div>
                    <div *ngIf="!node.features.length" class="no-features">
                        <mat-icon>info_outline</mat-icon>
                        <span>No features linked to this route</span>
                    </div>
                </div>
            </div>

            <div *ngIf="!filteredNodes.length" class="empty-state">
                <mat-icon>search_off</mat-icon>
                <span>No matching routes found</span>
            </div>
        </div>
    `,
    styles: [
        `
            :host {
                display: flex;
                flex-direction: column;
                height: 100%;
                overflow: hidden;
            }

            .search-bar {
                display: flex;
                align-items: center;
                gap: 6px;
                padding: 6px 10px;
                border-bottom: 1px solid rgba(0, 0, 0, 0.08);
                flex-shrink: 0;
            }
            .search-icon {
                color: rgba(0, 0, 0, 0.4);
                font-size: 18px;
                width: 18px;
                height: 18px;
            }
            .search-input {
                flex: 1;
                border: none;
                outline: none;
                font-size: 13px;
                background: transparent;
                color: rgba(0, 0, 0, 0.87);
                &::placeholder {
                    color: rgba(0, 0, 0, 0.38);
                }
            }
            .clear-icon {
                font-size: 16px;
                width: 16px;
                height: 16px;
                cursor: pointer;
                color: rgba(0, 0, 0, 0.4);
                &:hover {
                    color: rgba(0, 0, 0, 0.7);
                }
            }

            .summary-bar {
                display: flex;
                align-items: center;
                gap: 12px;
                padding: 6px 10px;
                border-bottom: 1px solid rgba(0, 0, 0, 0.08);
                flex-shrink: 0;
            }
            .summary-item {
                display: flex;
                align-items: center;
                gap: 3px;
                font-size: 12px;
                color: rgba(0, 0, 0, 0.6);
                mat-icon {
                    font-size: 14px;
                    width: 14px;
                    height: 14px;
                }
            }
            .spacer {
                flex: 1;
            }
            .action-icon {
                font-size: 18px;
                width: 18px;
                height: 18px;
                cursor: pointer;
                color: rgba(0, 0, 0, 0.4);
                &:hover {
                    color: rgba(0, 0, 0, 0.7);
                }
            }
            .help-icon {
                color: rgba(25, 118, 210, 0.6);
                &:hover {
                    color: #1976d2;
                }
            }

            .tree-container {
                flex: 1;
                overflow-y: auto;
                padding: 4px 0;

                /* Thin scrollbar */
                &::-webkit-scrollbar {
                    width: 4px;
                }
                &::-webkit-scrollbar-track {
                    background: transparent;
                }
                &::-webkit-scrollbar-thumb {
                    background: rgba(0, 0, 0, 0.15);
                    border-radius: 2px;
                }
            }

            .route-node {
                &:not(:last-child) {
                    border-bottom: 1px solid rgba(0, 0, 0, 0.04);
                }
            }

            .route-row {
                display: flex;
                align-items: center;
                gap: 6px;
                padding: 6px 10px;
                cursor: pointer;
                transition: background 0.15s;
                &:hover {
                    background: rgba(0, 0, 0, 0.03);
                }
            }
            .expand-icon {
                font-size: 18px;
                width: 18px;
                height: 18px;
                color: rgba(0, 0, 0, 0.4);
                flex-shrink: 0;
            }
            .route-icon {
                font-size: 16px;
                width: 16px;
                height: 16px;
                flex-shrink: 0;
            }
            .route-path {
                flex: 1;
                font-size: 13px;
                font-weight: 500;
                color: rgba(0, 0, 0, 0.87);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            .menu-label {
                display: flex;
                align-items: center;
                gap: 3px;
                font-size: 11px;
                padding: 1px 8px;
                border-radius: 10px;
                white-space: nowrap;
                flex-shrink: 0;
            }
            .menu-label-icon {
                font-size: 13px;
                width: 13px;
                height: 13px;
            }
            .section-label {
                font-size: 10px;
                padding: 1px 8px;
                border-radius: 10px;
                white-space: nowrap;
                flex-shrink: 0;
                font-weight: 500;
            }
            .feature-count {
                font-size: 11px;
                min-width: 18px;
                height: 18px;
                line-height: 18px;
                text-align: center;
                border-radius: 9px;
                background: rgba(0, 0, 0, 0.06);
                color: rgba(0, 0, 0, 0.5);
                flex-shrink: 0;
            }

            .features-list {
                padding: 2px 0 6px 40px;
            }

            .feature-row {
                display: flex;
                align-items: flex-start;
                gap: 6px;
                padding: 4px 10px;
                flex-wrap: wrap;
            }
            .feature-icon {
                font-size: 14px;
                width: 14px;
                height: 14px;
                margin-top: 1px;
                color: rgba(0, 0, 0, 0.4);
                flex-shrink: 0;
            }
            .feature-icon.primary {
                color: inherit;
            }
            .feature-icon.disabled {
                color: rgba(0, 0, 0, 0.2);
            }
            .feature-name {
                font-size: 12px;
                font-weight: 500;
                color: rgba(0, 0, 0, 0.7);
                white-space: nowrap;
                min-width: 100px;
            }
            .feature-name.disabled {
                color: rgba(0, 0, 0, 0.35);
                text-decoration: line-through;
            }

            .permission-chips {
                display: flex;
                flex-wrap: wrap;
                gap: 3px;
                flex: 1;
                min-width: 0;
            }
            .permission-chip {
                font-size: 10px;
                padding: 1px 6px;
                border-radius: 3px;
                white-space: nowrap;
            }
            .no-permissions {
                font-size: 10px;
                color: rgba(0, 0, 0, 0.3);
                font-style: italic;
            }
            .no-features {
                display: flex;
                align-items: center;
                gap: 6px;
                padding: 4px 10px;
                font-size: 12px;
                color: rgba(0, 0, 0, 0.35);
                mat-icon {
                    font-size: 14px;
                    width: 14px;
                    height: 14px;
                }
            }

            .empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: 8px;
                padding: 24px;
                color: rgba(0, 0, 0, 0.3);
                mat-icon {
                    font-size: 32px;
                    width: 32px;
                    height: 32px;
                }
                span {
                    font-size: 13px;
                }
            }

            /* Highlight search matches */
            :host ::ng-deep .hl {
                background: rgba(255, 235, 59, 0.5);
                border-radius: 2px;
                padding: 0 1px;
            }

            /* Dark theme */
            :host-context(.dark-theme) {
                .search-icon,
                .clear-icon,
                .action-icon {
                    color: rgba(255, 255, 255, 0.4);
                }
                .clear-icon:hover,
                .action-icon:hover {
                    color: rgba(255, 255, 255, 0.7);
                }
                .help-icon {
                    color: rgba(100, 181, 246, 0.6);
                    &:hover {
                        color: #64b5f6;
                    }
                }
                .search-input {
                    color: rgba(255, 255, 255, 0.87);
                    &::placeholder {
                        color: rgba(255, 255, 255, 0.38);
                    }
                }
                .search-bar,
                .summary-bar {
                    border-color: rgba(255, 255, 255, 0.08);
                }
                .summary-item {
                    color: rgba(255, 255, 255, 0.6);
                }
                .route-row:hover {
                    background: rgba(255, 255, 255, 0.05);
                }
                .route-node {
                    border-color: rgba(255, 255, 255, 0.04);
                }
                .expand-icon {
                    color: rgba(255, 255, 255, 0.4);
                }
                .route-path {
                    color: rgba(255, 255, 255, 0.87);
                }
                .feature-count {
                    background: rgba(255, 255, 255, 0.1);
                    color: rgba(255, 255, 255, 0.5);
                }
                .feature-icon {
                    color: rgba(255, 255, 255, 0.4);
                }
                .feature-icon.disabled {
                    color: rgba(255, 255, 255, 0.2);
                }
                .feature-name {
                    color: rgba(255, 255, 255, 0.7);
                }
                .feature-name.disabled {
                    color: rgba(255, 255, 255, 0.35);
                }

                .no-permissions,
                .no-features {
                    color: rgba(255, 255, 255, 0.3);
                }
                .empty-state {
                    color: rgba(255, 255, 255, 0.3);
                }
                .tree-container::-webkit-scrollbar-thumb {
                    background: rgba(255, 255, 255, 0.15);
                }

                :host ::ng-deep .hl {
                    background: rgba(255, 235, 59, 0.3);
                }
            }
        `,
    ],
})
export class FeatureMapWidgetComponent implements OnChanges {
    @Input() data!: OrgWidgetData;

    constructor(
        private dialog: MatDialog,
        public colorsService: OrgEntityColorsService
    ) {}

    searchTerm = '';
    allNodes: RouteFeatureNode[] = [];
    filteredNodes: RouteFeatureNode[] = [];
    totalFeatures = 0;
    totalPermissions = 0;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['data'] && this.data) {
            this.buildTree();
            this.applyFilter();
        }
    }

    /** Build the route → feature → permission tree from widgetData. */
    private buildTree(): void {
        const routes = this.data.routes || [];
        const featureMap = new Map<
            string,
            {
                key: string;
                name: string;
                permissions: string[];
                enabled: boolean;
                description?: string;
                permissionDescriptions?: Record<string, string>;
            }
        >();

        // Index all org features by key
        for (const f of this.data.features || []) {
            const d = f.data as Record<string, unknown>;
            featureMap.set(f.key, {
                key: f.key,
                name: (d['name'] as string) || f.key,
                permissions: (d['permissions'] as string[]) || [],
                enabled: (d['enabled'] as boolean) !== false,
                description: (d['description'] as string) || undefined,
                permissionDescriptions: (d['permissionDescriptions'] as Record<string, string>) || undefined,
            });
        }

        // Index menu items by route path for quick lookup
        const menuMap = new Map<string, OrgWidgetMenuItem>();
        for (const mi of this.data.menuItems || []) {
            menuMap.set(mi.routePath.toLowerCase(), mi);
        }

        // Build nodes
        const permSet = new Set<string>();
        const featSet = new Set<string>();

        this.allNodes = routes
            .map((r) => {
                const rd = r.data as Record<string, unknown>;
                const route = (rd['route'] as string) || r.key;
                const routeFeature = (rd['routeFeature'] as string) || '';
                const features = (rd['features'] as string[]) || [];
                const systemRoute = (rd['systemRoute'] as boolean) || false;
                const publicRoute = (rd['publicRoute'] as boolean) || false;

                // Collect unique feature nodes for this route
                const seen = new Set<string>();
                const featureNodes: FeatureNode[] = [];

                // Primary feature first (only if enabled)
                if (routeFeature && !seen.has(routeFeature)) {
                    seen.add(routeFeature);
                    const feat = featureMap.get(routeFeature);
                    if (feat && feat.enabled) {
                        featureNodes.push({ ...feat, isPrimary: true });
                        featSet.add(feat.key);
                        feat.permissions.forEach((p) => permSet.add(p));
                    }
                }

                // Additional features (only if enabled)
                for (const fKey of features) {
                    if (seen.has(fKey)) continue;
                    seen.add(fKey);
                    const feat = featureMap.get(fKey);
                    if (feat && feat.enabled) {
                        featureNodes.push({ ...feat, isPrimary: false });
                        featSet.add(feat.key);
                        feat.permissions.forEach((p) => permSet.add(p));
                    }
                }

                // Look up the menu item for this route
                const menuItem = menuMap.get(route.toLowerCase());

                // Extract section from route path: /app/organization/dashboard → 'Organization'
                const section = this.extractSection(route);

                return {
                    route,
                    routeFeature,
                    systemRoute,
                    publicRoute,
                    features: featureNodes,
                    expanded: false,
                    menuItem,
                    section,
                } as RouteFeatureNode;
            })
            .filter((node) => node.features.length > 0 || node.publicRoute) // Show public routes even without features
            .sort((a, b) => a.route.localeCompare(b.route));

        this.totalFeatures = featSet.size;
        this.totalPermissions = permSet.size;
    }

    /** Filter nodes by search term (routes, features, or permissions). */
    applyFilter(): void {
        const term = this.searchTerm.trim().toLowerCase();
        if (!term) {
            this.filteredNodes = this.allNodes;
            return;
        }

        this.filteredNodes = this.allNodes
            .filter((node) => {
                if (node.route.toLowerCase().includes(term)) return true;
                if (node.menuItem?.name.toLowerCase().includes(term)) return true;
                return node.features.some(
                    (f) =>
                        f.name.toLowerCase().includes(term) ||
                        f.key.toLowerCase().includes(term) ||
                        f.permissions.some((p) => p.toLowerCase().includes(term))
                );
            })
            .map((node) => ({ ...node, expanded: true })); // auto-expand matched nodes
    }

    /** Highlight matching text in the search results. */
    highlight(text: string): string {
        if (!this.searchTerm.trim()) return text;
        const escaped = this.searchTerm.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(${escaped})`, 'gi');
        return text.replace(regex, '<span class="hl">$1</span>');
    }

    expandAll(): void {
        this.filteredNodes.forEach((n) => (n.expanded = true));
    }
    collapseAll(): void {
        this.filteredNodes.forEach((n) => (n.expanded = false));
    }

    trackByRoute(_: number, item: RouteFeatureNode): string {
        return item.route;
    }
    trackByFeature(_: number, item: FeatureNode): string {
        return item.key;
    }

    /** Extract section name from route path: /app/organization/dashboard → 'Organization' */
    private extractSection(route: string): string | undefined {
        // Route format: /app/{section}/{page}
        const parts = route.replace(/^\//, '').split('/');
        if (parts.length < 2 || parts[0] !== 'app') return undefined;
        // Convert kebab-case to Title Case: "management-tools" → "Management Tools"
        return parts[1]
            .split('-')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ');
    }

    openSecurityFlow(event: Event): void {
        event.stopPropagation();
        this.dialog.open(SecurityFlowDialogComponent, {
            panelClass: 'security-flow-dialog-panel',
            width: '92vw',
            height: '92vh',
            maxWidth: '98vw',
            maxHeight: '98vh',
            autoFocus: false,
        });
    }
}
